'use client'

import {
  type ChangeEvent,
  type CSSProperties,
  type FormEvent,
  useId,
  useState,
} from 'react'
import { BrandUrlInputLogo } from '@/components/brand-setup/brand-url-input-logo'
import { useOpenAuthModal } from '@/components/marketing/v2/use-auth-modal'
import { useMediaQuery } from '@/hooks/use-media-query'
import {
  LANDING_BRAND_DOMAIN_PARAM,
  saveLandingBrandDomain,
  toLandingBrandDomain,
} from '@/lib/onboarding/landing-brand-domain'
import { homeButtonClasses } from '../home-button'
import { HOME_LINKS } from '../home-links'
import { HERO_BRAND_FIELD } from './hero-content'

/**
 * The white pill holding the field and its button side by side at every
 * width: 1px ring, hairline drop, 6px orange halo; the ring turns accent
 * while the field has focus. The pill is 52px around the standard 40px
 * button; on phones the button drops its arrow so the placeholder keeps its
 * room.
 */
const FORM_PILL =
  'relative flex h-[52px] w-full items-center gap-1 rounded-[70px] bg-mk-card py-1.5 pr-1.5 pl-3.5 shadow-[0_0_0_1px_var(--mk-ring),var(--mk-button-shadow),0_0_0_6px_#f04e0a14] transition-shadow duration-quick ease-smooth-out has-[input:focus]:shadow-[0_0_0_1px_var(--mk-accent),var(--mk-button-shadow),0_0_0_6px_#f04e0a2e] sm:gap-2 sm:pl-5'

/**
 * The sheen that runs round the pill's rim: the shimmer the prominent CTA
 * button uses (`prominent-cta-button.tsx`), sized 3px larger than the pill
 * so only a thin travelling band of accent shows past the white, blurred so
 * it reads as light rather than a line. It lives behind the form and under
 * the halo, so the pill itself and its focus ring are unchanged.
 */
const SHIMMER_STYLE = {
  '--spread': '90deg',
  '--shimmer-color': 'var(--mk-accent)',
  '--speed': '4s',
} as CSSProperties

function PillShimmer() {
  return (
    <span
      aria-hidden="true"
      style={SHIMMER_STYLE}
      className="pointer-events-none absolute -inset-[3px] -z-10 overflow-hidden rounded-[70px] opacity-80 blur-[2px] [container-type:size] motion-reduce:hidden"
    >
      <span className="absolute inset-0 h-[100cqh] animate-shimmer-slide [aspect-ratio:1]">
        <span className="absolute -inset-full animate-spin-around [background:conic-gradient(from_calc(270deg-(var(--spread)*0.5)),transparent_0,var(--shimmer-color)_var(--spread),transparent_var(--spread))]" />
      </span>
    </span>
  )
}

/**
 * The hero's website field. With JavaScript it
 * stays on the page: a valid website is saved for the onboarding brand step
 * (`lib/onboarding/landing-brand-domain.ts`) and the auth modal opens in
 * place (`useOpenAuthModal`); an empty field opens it straight away, and
 * anything else stays put with a native validation message. Without
 * JavaScript it is a plain form GET to `/sign-up?website=…`, whose page
 * stores the website the same way. Once what's typed reads as a domain, that
 * brand's logo fades in as a small circle beside the button (the same
 * context.dev Logo Link lookup, favicon fallback and debounce onboarding's
 * brand field uses).
 */
export function BrandUrlForm() {
  const openAuth = useOpenAuthModal()
  const inputId = useId()
  const [website, setWebsite] = useState('')
  // Below Tailwind `sm` the field cannot fit the full line, so it asks for
  // less. The server renders the full line (the desktop default) and the
  // phone swaps once it has measured itself.
  const isPhone = useMediaQuery('(max-width: 639px)', false)
  const placeholder = isPhone
    ? HERO_BRAND_FIELD.placeholderPhone
    : HERO_BRAND_FIELD.placeholder

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    // A new value gets a fresh chance: drop the "not a website" message.
    event.currentTarget.setCustomValidity('')
    setWebsite(event.currentTarget.value)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const field = event.currentTarget.elements.namedItem(
      LANDING_BRAND_DOMAIN_PARAM
    )
    const value = field instanceof HTMLInputElement ? field.value : ''

    if (value.trim() === '') {
      openAuth()
      return
    }

    const domain = toLandingBrandDomain(value)
    if (domain === null) {
      if (field instanceof HTMLInputElement) {
        field.setCustomValidity(HERO_BRAND_FIELD.invalidMessage)
        field.reportValidity()
      }
      return
    }

    saveLandingBrandDomain(domain)
    openAuth()
  }

  return (
    <div className="relative z-0 w-full max-w-lg">
      <PillShimmer />
      <form
        action={HOME_LINKS.signUp}
        method="get"
        onSubmit={handleSubmit}
        className={FORM_PILL}
      >
        <label htmlFor={inputId} className="sr-only">
          {HERO_BRAND_FIELD.label}
        </label>
        <input
          id={inputId}
          name={LANDING_BRAND_DOMAIN_PARAM}
          type="text"
          inputMode="url"
          autoComplete="url"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="go"
          placeholder={placeholder}
          onChange={handleChange}
          className="h-6 w-full min-w-0 flex-1 text-ellipsis bg-transparent font-mk-body text-[16px] text-mk-ink leading-6 tracking-[-0.01em] outline-none placeholder:text-mk-faint"
        />
        <BrandUrlInputLogo
          domainInput={website}
          className="size-7 overflow-clip rounded-full bg-white ring-1 ring-black/[0.08]"
          imageClassName="size-full rounded-full object-cover"
        />
        <button
          type="submit"
          className={homeButtonClasses({
            variant: 'brand',
            size: 'md',
            className: 'gap-1 px-4 sm:px-5',
          })}
        >
          {HERO_BRAND_FIELD.submit}
          <span aria-hidden="true" className="hidden sm:inline">
            →
          </span>
        </button>
      </form>
      <p className="mt-4 text-center font-mk-body text-mk-body text-sm leading-5">
        {HERO_BRAND_FIELD.nextStep}
      </p>
    </div>
  )
}
